import React, { useEffect, useMemo, useState } from 'react';
import AuthLayout from '../components/layout/AuthLayout.jsx';
import {
    useGetNotificationsTypesQuery,
    useLazyGetNotificationsQuery,
    useMarkNotificationMutation,
    useMassDeleteNotificationsMutation
} from '../api/notifications.js';
import BaseDropdown from '../components/form/BaseDropdown.jsx';
import ThreeDotsLoader from '../components/misc/ThreeDotsLoader.jsx';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import { PERMISSION_OWNED_ORGANIZATIONS, PERMISSION_PARTNER_ORGANIZATIONS } from '../utils/constants.js';
import IconMultiSelect from '../components/images/IconMultiSelect.jsx';
import useCurrentUser from '../hooks/useCurrentUser.js';
import { useErrorToast } from '../hooks/useToast.jsx';
import BaseButton from '../components/form/BaseButton.jsx';
import IconDelete from '../components/images/IconDelete.jsx';
import { twMerge } from 'tailwind-merge';
import { link, renderIcon } from '../utils/helper.js';
import IconEye from '../components/images/IconEye.jsx';

const Notifications = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const [notifications, setNotifications] = useState([]);
    const [refetch, setRefetch] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);
    const [append, setAppend] = useState(false);
    const [hasMorePages, setHasMorePages] = useState(true);
    const [notificationsData, setNotificationsData] = useState([]);
    const [selectMode, setSelectMode] = useState(false);
    const [confirmMassDelete, setConfirmMassDelete] = useState(false);
    const [selectedNotifications, setSelectedNotifications] = useState([]);
    const [byType, setByType] = useState(false);

    const requestParams = useMemo(() => {
        if (refetch) {
            setRefetch(false);
        }

        return {
            filter,
            page,
            per_page: 10
        };
    }, [filter, page, refetch]);

    const [getNotificationsData] = useLazyGetNotificationsQuery({});

    const [markNotification] = useMarkNotificationMutation();
    const [massDeleteNotifications] = useMassDeleteNotificationsMutation();

    const { data: typesData, isLoading: typesLoading } = useGetNotificationsTypesQuery();

    const typeOptions = useMemo(
        () =>
            typesData?.response?.notification_types?.map((item) => ({
                label: item.label,
                value: item.value
            })) || [],
        [typesData]
    );

    const dropdownOptions = useMemo(() => [{ label: 'All', value: '' }, ...typeOptions], [typeOptions]);

    const filterNotifications = (value) => {
        const newFilter = typeof value === 'object' && value !== null ? value.value : value;
        if (newFilter !== filter) {
            setFilter(newFilter);
            setPage(1);
            setAppend(false);
            setHasMorePages(true);
            setNotifications([]);
        }
    };

    const resetSearchAndFilters = () => {
        setFilter('');
        setPage(1);
    };

    const changeSelectionMode = () => {
        setSelectMode(!selectMode);
        setSelectedNotifications([]);
    };

    const handleMarkAsRead = async (ids, status) => {
        if (status === 'read') return;
        try {
            await markNotification({ ids, status: 'read' });
            resetSearchAndFilters();
            setRefetch(true);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const selectAllAfterType = (checked) => {
        const selectedTypes = new Set(
            notifications.filter((item) => selectedNotifications.includes(item.id)).map((item) => item.type)
        );

        if (selectedTypes.size === 0) return;

        if (checked) {
            setSelectedNotifications((prev) => {
                const idsToAdd = notifications.filter((item) => selectedTypes.has(item.type)).map((item) => item.id);

                return [...new Set([...prev, ...idsToAdd])];
            });
        } else {
            setSelectedNotifications((prev) =>
                prev.filter((id) => {
                    const item = notifications.find((item) => item.id === id);
                    return item && !selectedTypes.has(item.type);
                })
            );
        }
        setByType(checked);
    };

    const submitDeleteNotifications = async () => {
        try {
            await massDeleteNotifications({ ids: selectedNotifications }).then((apiResponse) => {
                if (apiResponse.data?.success) {
                    resetSearchAndFilters();
                    setRefetch(true);
                } else {
                    useErrorToast(apiResponse.data?.message);
                }
            });
        } catch (error) {
            console.error('Failed to delete notifications:', error);
        }

        setSelectMode(false);
        setConfirmMassDelete(false);
        setSelectedNotifications([]);
    };

    useEffect(() => {
        setIsLoading(true);

        (async () => {
            const result = await getNotificationsData(requestParams);
            setNotificationsData(result.data);
        })();
    }, [getNotificationsData, requestParams]);

    useEffect(() => {
        if (notificationsData?.response) {
            const incoming = notificationsData.response.notifications || [];

            if (append) {
                setNotifications((prev) => {
                    const map = new Map();
                    [...prev, ...incoming].forEach((item) => map.set(item.id, item));
                    return Array.from(map.values());
                });
            } else {
                setNotifications(incoming);
            }

            setIsLoading(false);
            setNotificationsData({});
            setHasMorePages(notificationsData.response.has_more_pages);
            setAppend(false);
        }
    }, [notificationsData, append]);

    useEffect(() => {
        let processing = false;

        const handleScroll = debounce(() => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                document.documentElement.offsetHeight
            ) {
                if (hasMorePages && !isLoading && !processing) {
                    processing = true;
                    setPage((prev) => prev + 1);
                    setAppend(true);
                    setTimeout(() => {
                        processing = false;
                    }, 500);
                }
            }
        }, 300);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [hasMorePages, isLoading]);

    const showNoNotificationsText = !isLoading && notifications.length === 0 && !hasMorePages;

    return (
        <AuthLayout title='Notifications' backRoute='/' mainClassNames='flex flex-col'>
            <div className='max-w-3xl mx-auto w-full h-full pt-4 px-6'>
                <div className='flex justify-between items-center gap-4 mb-4'>
                    <BaseDropdown
                        name='notificationType'
                        placeholder='All'
                        value={filter}
                        setValue={filterNotifications}
                        options={dropdownOptions}
                        disabled={typesLoading}
                    />
                    {(!!userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.delete ||
                        !!userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.delete) && (
                        <div className='flex justify-between'>
                            <div onClick={() => changeSelectionMode()} className='cursor-pointer'>
                                <IconMultiSelect className='w-6 h-6 fill-theme1-dark' />
                            </div>
                        </div>
                    )}
                </div>
                {!isLoading && selectMode && !confirmMassDelete && selectedNotifications.length > 0 && (
                    <div className='py-2.5 font-bold text-theme1-title2 flex items-center gap-2'>
                        <div className='h-6 flex-shrink-0 mt-[2px]'>
                            <input
                                id='select-all-by-types'
                                type='checkbox'
                                className={twMerge(
                                    'h-6 w-6 appearance-none cursor-pointer rounded-full border border-theme1-primary relative',
                                    'checked:bg-theme1-primary',
                                    'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                    'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                    "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmarkWhite.svg')]"
                                )}
                                onChange={() => selectAllAfterType(!byType)}
                                checked={byType}
                            />
                        </div>
                        <label htmlFor='select-all-by-types' className='cursor-pointer select-none leading-6'>
                            {!byType ? (
                                <span>
                                    SELECT BY ALL TYPES{' '}
                                    <span className='font-semibold'>
                                        (
                                        {[
                                            ...new Set(
                                                notifications
                                                    .filter((item) => selectedNotifications.includes(item.id))
                                                    .map((item) => item.type)
                                            )
                                        ].join(', ')}
                                        )
                                    </span>
                                </span>
                            ) : (
                                'DESELECT ALL'
                            )}
                        </label>
                    </div>
                )}
                {isLoading && page === 1 ? (
                    <ThreeDotsLoader />
                ) : showNoNotificationsText ? (
                    <div className='text-theme1-title2'>No notifications found</div>
                ) : (
                    <ul className={'mb-4'}>
                        {notifications.map((notification, index) => (
                            <li key={notification.id} className={'flex py-2'}>
                                {selectMode && (
                                    <div className={'w-auto mt-5 mr-5'}>
                                        <input
                                            id={'notification-' + notification.id}
                                            type='checkbox'
                                            className={twMerge(
                                                'h-6 w-6 appearance-none cursor-pointer rounded-full border border-theme1-primary',
                                                'checked:bg-theme1-tan relative',
                                                'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                                'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                                "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                                            )}
                                            value={notification.id}
                                            onChange={(e) => {
                                                const { checked } = e.target;
                                                setByType(false);
                                                if (checked) {
                                                    setSelectedNotifications((prevState) => [
                                                        ...prevState,
                                                        notification.id
                                                    ]);
                                                    if (notifications.length === selectedNotifications.length + 1) {
                                                        setByType(true);
                                                    }
                                                } else {
                                                    setSelectedNotifications((prevState) =>
                                                        prevState.filter((id) => id !== notification.id)
                                                    );
                                                }
                                            }}
                                            checked={selectedNotifications.includes(notification.id)}
                                        />
                                    </div>
                                )}
                                {selectMode ? (
                                    <div className={'block'}>
                                        <div
                                            className={`${notification.status === 'unread' ? 'bg-theme1-white' : 'bg-theme1-pinkGray'} flex rounded-3xl shadow-lg border border-theme1-border1 p-[16px]`}
                                        >
                                            <div className='w-auto'>
                                                {renderIcon(
                                                    notification.type,
                                                    notification.status === 'unread'
                                                        ? 'bg-theme1-gold'
                                                        : 'bg-theme1-disabled'
                                                )}
                                            </div>
                                            <div className='grid'>
                                                <span
                                                    className={`font-bold text-theme1-title2 ${
                                                        notification.status === 'unread' ? 'font-extrabold' : ''
                                                    }`}
                                                >
                                                    {notification.title}
                                                </span>
                                                <span className='text-theme1-text1'>{notification.message}</span>
                                            </div>
                                            <div className={'w-auto'}>
                                                <span
                                                    className={`inline-block w-3 h-3 rounded-full ${notification.status === 'unread' ? 'bg-theme1-unread' : 'bg-theme1-text1'}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <Link
                                        to={link(notification)}
                                        onClick={() => handleMarkAsRead([notification.id], notification.status)}
                                        className={'block'}
                                    >
                                        <div
                                            className={`${notification.status === 'unread' ? 'bg-theme1-white' : 'bg-theme1-pinkGray'} flex rounded-3xl shadow-lg border border-theme1-border1 p-[16px]`}
                                        >
                                            <div className='w-auto'>
                                                {renderIcon(
                                                    notification.type,
                                                    notification.status === 'unread'
                                                        ? 'bg-theme1-gold'
                                                        : 'bg-theme1-disabled'
                                                )}
                                            </div>
                                            <div className='grid'>
                                                <span
                                                    className={`font-bold text-theme1-title2 ${
                                                        notification.status === 'unread' ? 'font-extrabold' : ''
                                                    }`}
                                                >
                                                    {notification.title}
                                                </span>
                                                <span className='text-theme1-text1'>{notification.message}</span>
                                            </div>
                                            <div className={'w-auto'}>
                                                <span
                                                    className={`inline-block w-3 h-3 rounded-full ${notification.status === 'unread' ? 'bg-theme1-unread' : 'bg-theme1-text1'}`}
                                                />
                                            </div>
                                        </div>
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {isLoading && page > 1 && (
                    <div className={'my-4'}>
                        <ThreeDotsLoader />
                    </div>
                )}
                {selectMode && (
                    <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>
                        {confirmMassDelete ? (
                            <div>
                                <div className='text-white font-semibold text-lg text-center'>
                                    Are you sure you want to delete {selectedNotifications.length} selected
                                    notifications?
                                </div>
                                <div className='flex justify-between items-center gap-4 mt-6'>
                                    <BaseButton
                                        className='btn-red-outline !text-theme1-white !border-theme1-white'
                                        textValue={'Cancel'}
                                        onClick={() => setConfirmMassDelete(false)}
                                    />
                                    <BaseButton
                                        className='btn-red-outline !text-theme1-white !border-theme1-white'
                                        textValue={'Delete'}
                                        onClick={() => submitDeleteNotifications()}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className='flex justify-between items-center'>
                                <div className={'flex gap-3'}>
                                    <div className='cursor-pointer' onClick={() => setConfirmMassDelete(true)}>
                                        {selectedNotifications.length > 0 && <IconDelete />}
                                    </div>
                                    <div
                                        className='cursor-pointer'
                                        onClick={() => handleMarkAsRead(selectedNotifications, 'unread')}
                                    >
                                        {selectedNotifications.length > 0 && <IconEye />}
                                    </div>
                                </div>
                                <div className='text-white font-semibold text-lg'>
                                    {selectedNotifications.length} Selected
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AuthLayout>
    );
};

export default Notifications;
